import ReplyWithTextFeatures from '../contract/ReplyWithTextFeatures';
import formatTextToHTML from 'owa-mail-compose-actions/lib/utils/formatTextToHTML';
import { createDefaultComposeFormatWrapper } from 'owa-mail-compose-actions/lib/utils/getDefaultComposeContentBlock';
import { ComposeViewState } from 'owa-mail-compose-store';
import { isReadingPanePositionBottom } from 'owa-mail-layout/lib/selectors/readingPanePosition';
import { getSelectedTableView } from 'owa-mail-list-store';
import { isDeepLink } from 'owa-popout';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { trace } from 'owa-trace';
import { action } from 'satcheljs/lib/legacy';
import {
    findInlineComposeViewState,
    getDefaultBodyType,
    lazyReplyToMessageAndCreateViewState,
    lazyFindComposePlugins,
    TEXT_BODY_TYPE,
} from 'owa-mail-compose-actions';

const QUOTE_CLASS = 'userQuote';
const SMART_REPLY_CLASS = 'smartReply';

export default action('replyWithText')(async function replyWithText(
    itemId: string,
    conversationId: string,
    selection: string,
    feature: ReplyWithTextFeatures
): Promise<ComposeViewState> {
    const elementClass = getElementClassName(feature);
    const elementId = elementClass + Math.round(Math.random() * 1000000).toString();

    // Don't use HTML if we're in plain text mode.
    const isPlainText: boolean = getDefaultBodyType() === TEXT_BODY_TYPE;
    const selectionHtmlNode = createElementForText(feature, elementId, selection);
    const selectionAsPlainText: string = '\n' + selection + '\n';

    let contentToInsert: string;
    if (isPlainText) {
        contentToInsert = selectionAsPlainText;
    } else {
        const textDiv = createDefaultComposeFormatWrapper();
        textDiv.appendChild(selectionHtmlNode);
        contentToInsert = textDiv.outerHTML;
    }

    const existingViewState = findInlineComposeViewState(conversationId);
    let viewState = null;

    if (existingViewState) {
        // If the compose view state already exists, this means the compose window is open
        // Since the compose window is already open, just insert quoted content into email
        viewState = existingViewState;
    } else {
        // Otherwise, create a new reply with quoted content
        const replyToMessageAndCreateViewState = await lazyReplyToMessageAndCreateViewState.import();
        viewState = await replyToMessageAndCreateViewState({
            referenceItemOrId: itemId,
            isReplyAll: true,
            useFullCompose:
                getListViewType() == ReactListViewType.Message ||
                isReadingPanePositionBottom() ||
                isDeepLink(),
            conversationId: conversationId,
            body: contentToInsert,
            instrumentationContexts: [],
            useCortanaSchedulingAssistant: feature == ReplyWithTextFeatures.SmartTimeWithCortana,
        });
    }

    if (viewState) {
        const findComposePlugins = await lazyFindComposePlugins.import();
        if (findComposePlugins) {
            const composePlugins = findComposePlugins(viewState);
            if (composePlugins && composePlugins.contentOperationPlugin) {
                if (existingViewState) {
                    composePlugins.contentOperationPlugin.insertContentIntoEditor(
                        isPlainText ? selectionAsPlainText : selectionHtmlNode.outerHTML,
                        getEventName(feature),
                        false /* belowExistingContent */,
                        true /* setFocusToEditor */
                    );
                } else if (!isPlainText) {
                    composePlugins.contentOperationPlugin.moveCaretBelowHtmlElement(elementId);
                }
            }
        }
    }

    return viewState;
});

function getElementClassName(feature: ReplyWithTextFeatures): string {
    switch (feature) {
        case ReplyWithTextFeatures.UserHighlighting:
            return QUOTE_CLASS;
        case ReplyWithTextFeatures.SmartReply:
        case ReplyWithTextFeatures.SmartTimeWithCortana:
            return SMART_REPLY_CLASS;
        default:
            trace.warn('replyWithText_getElementClassName: Unsupported feature inputted');
            return '';
    }
}

function getEventName(feature: ReplyWithTextFeatures): string {
    switch (feature) {
        case ReplyWithTextFeatures.UserHighlighting:
            return 'UserHighlighting';
        case ReplyWithTextFeatures.SmartReply:
        case ReplyWithTextFeatures.SmartTimeWithCortana:
            return 'SmartReply';
        default:
            trace.warn('replyWithText_getEventName: Unsupported feature inputted');
            return '';
    }
}

function createElementForText(
    feature: ReplyWithTextFeatures,
    elementId: string,
    textForMarkup: string
): HTMLElement {
    switch (feature) {
        case ReplyWithTextFeatures.UserHighlighting:
            return createQuoteTextElement(elementId, textForMarkup);
        case ReplyWithTextFeatures.SmartReply:
        case ReplyWithTextFeatures.SmartTimeWithCortana:
            return createSmartReplyElement(elementId, textForMarkup);
        default:
            trace.warn('[createHtmlForText] Unsupported feature');
            return null;
    }
}

function createQuoteTextElement(quoteId: string, quoteText: string): HTMLElement {
    // Convert the quoted text to HTML to protect against XSS
    quoteText = formatTextToHTML(quoteText);

    // TODO: Reference the same function as the blockquote button
    // in the ribbon (VSO 36144)
    const quoteElement = document.createElement('blockquote');
    quoteElement.id = quoteId;
    quoteElement.className = QUOTE_CLASS;
    quoteElement.style.borderLeft = '3px solid #C8C8C8';
    quoteElement.style.paddingLeft = '1ex';
    quoteElement.style.marginLeft = '0.8ex';
    quoteElement.style.color = '#666666';

    // OutlookWeb:54526
    // tslint:disable-next-line:no-inner-html
    quoteElement.innerHTML = quoteText;

    const elementWrapper = document.createElement('div');
    elementWrapper.appendChild(document.createElement('br'));
    elementWrapper.appendChild(quoteElement);
    elementWrapper.appendChild(document.createElement('br'));

    return elementWrapper;
}

function createSmartReplyElement(smartReplyId: string, smartReplyText: string): HTMLElement {
    const smartReplyElement = document.createElement('span');
    smartReplyElement.id = smartReplyId;
    smartReplyElement.innerText = smartReplyText;

    const elementWrapper = document.createElement('div');
    elementWrapper.appendChild(smartReplyElement);

    return elementWrapper;
}

function getListViewType(): ReactListViewType {
    let listViewType = null;
    const tableView = getSelectedTableView();
    if (tableView && tableView.tableQuery) {
        listViewType = tableView.tableQuery.listViewType;
    }
    return listViewType;
}
